import { createFocusTrap } from "focus-trap";
import { LitElement } from "lit";
import { customElement, query, queryAll, state } from "lit/decorators.js";

import "./_language-picker-label";
import "./_menu-button";
import "./_search";
import "./_user-avatar";
import "./_user-menu";
import { MQL_LG, MQL_MOTION } from "../../constants";
import { getCurrentUser } from "../../data/user";
import {
  lockDocumentScrolling,
  polyfillDeclarativeShadowDOM,
  unlockDocumentScrolling,
} from "../../utils/dom";
import { requestIdleCallback } from "../../utils/timing";
import { toPx } from "../../utils/units";
import { GlobalHeaderSubmenu } from "./_submenu";
import {
  MENU_CLOSE_EVENT_NAME,
  SEARCH_CLOSE_EVENT_NAME,
  SEARCH_OPEN_EVENT_NAME,
  SUBMENU_CLOSE_EVENT_NAME,
  SUBMENU_OPEN_EVENT_NAME,
} from "./global-header.constants";
import { styles } from "./global-header.styles";

import type { GlobalHeaderMenuButton } from "./_menu-button";
import type { GlobalHeaderSearch } from "./_search";
import type { GlobalHeaderUserAvatar } from "./_user-avatar";
import type { GlobalHeaderUserMenu } from "./_user-menu";
import type { FocusTrap, Options } from "focus-trap";

@customElement("bp-global-header")
export class GlobalHeader extends LitElement {
  static styles = styles;

  @query("#container")
  $container?: HTMLElement;
  @query("header")
  $header?: HTMLElement;

  @query("#logo")
  $logo?: HTMLElement;
  @query("#main-menu")
  $mainMenu?: HTMLElement;

  @query("#menu-toggle-button")
  $menuToggleButton?: GlobalHeaderMenuButton;

  @query("#primary-nav")
  $nav?: HTMLElement;

  @query("#search")
  $search?: GlobalHeaderSearch;

  @queryAll("bp-global-header-submenu")
  $submenus?: NodeListOf<GlobalHeaderSubmenu>;

  @queryAll("[aria-controls]")
  $submenuTriggers?: NodeListOf<HTMLButtonElement>;

  @query("bp-global-header-user-avatar")
  $userAvatar?: GlobalHeaderUserAvatar;

  @query("bp-global-header-user-menu")
  $userMenu?: GlobalHeaderUserMenu;

  @query("#user-submenu")
  $userSubmenu?: GlobalHeaderSubmenu;

  #motionMql = MQL_MOTION;

  @state()
  useAnimations = !this.#motionMql.matches;

  // Media Query Lists
  #desktopMql = MQL_LG;

  @state()
  useDesktopLayout = this.#desktopMql.matches;

  get animationOptions(): KeyframeAnimationOptions {
    return {
      duration: this.useAnimations ? 240 : 0,
      easing: "cubic-bezier(0.2, 0.0, 0, 1.0)",
      fill: "forwards",
    };
  }

  // Focus-related properties
  #focusTrap: FocusTrap | null = null;

  #lastFocusedMenuItem: HTMLButtonElement | null = null;

  ////////////////
  // LIFECYCLE  //
  ////////////////

  constructor() {
    super();
    polyfillDeclarativeShadowDOM(this);
  }

  /**
   * Activates a focus trap so that tabbing with a keyboard
   * cycles through focusable elements within the target
   * element.
   */
  activateFocusTrap(
    $element?: (HTMLElement | null)[] | HTMLElement | null,
    options?: Options,
  ) {
    const $elements = (Array.isArray($element) ? $element : [$element]).filter(
      (el): el is HTMLElement => Boolean(el) && !el?.inert,
    );

    // Check that a valid element or elements are provided
    if ($elements.length === 0) {
      this.#focusTrap = null;

      return;
    }

    this.#focusTrap = createFocusTrap(
      $elements as HTMLElement[],
      Object.assign(
        {
          allowOutsideClick: true,
          tabbableOptions: {
            getShadowRoot: true,
          },
          onDeactivate: () => {
            if ($element instanceof GlobalHeaderSubmenu) {
              $element.close();
            }
          },
        } satisfies Options,
        options,
      ),
    ).activate();
  }

  async checkAuth() {
    const info = await getCurrentUser();
    this.$userMenu?.setUserInfo(info);
    this.$userAvatar?.setUserInfo(info);
    this.requestUpdate();
  }

  /**
   * Closes all the submenus.
   */
  async closeAllSubmenus(options?: { exclude?: string[] }) {
    await Promise.all(
      Array.from(this.$submenus ?? []).map((x) => {
        if (!options?.exclude?.includes(x.id)) {
          return this.closeSubmenu(x.id);
        }

        return Promise.resolve();
      }),
    );
  }

  /**
   * Closes the main menu (on mobile).
   */
  closeMenu() {
    if (this.useDesktopLayout) return;

    this.$header?.setAttribute("data-nav-open", "false");
    this.$search?.removeAttribute("inert");

    requestIdleCallback(() => {
      this.$menuToggleButton?.reset();
      this.deactivateFocusTrap();
      this.closeAllSubmenus();
      this.setA11yAttributes();
      unlockDocumentScrolling();
    });
  }

  //////////////
  // METHODS  //
  //////////////

  /**
   * Closes the search box.
   */
  closeSearch() {
    this.$search?.deactivate();

    this.$header?.setAttribute("data-search-open", "false");
    this.$menuToggleButton?.setAttribute("active", "false");

    unlockDocumentScrolling();
  }

  /**
   * Closes a specific submenu.
   */
  async closeSubmenu(id: string, options?: { $trigger?: HTMLElement }) {
    this.$header?.setAttribute("data-submenu-open", "false");
    this.$container?.scrollTo(0, 0);

    this.#lastFocusedMenuItem?.focus();

    const $parent =
      options?.$trigger ??
      this.shadowRoot?.querySelector<HTMLLIElement>(`[aria-controls="${id}"]`);

    $parent?.closest("li")?.removeAttribute("data-submenu-open");

    // Show the submenu
    const $submenu = this.shadowRoot?.querySelector<GlobalHeaderSubmenu>(
      `#${id}`,
    );

    $submenu?.setAttribute("inert", "");

    if (this.useDesktopLayout) {
      this.deactivateFocusTrap();
    }

    $parent?.setAttribute("aria-expanded", "false");

    document.removeEventListener("click", this.handleDocumentClick);
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener(
      MENU_CLOSE_EVENT_NAME,
      this.handleMenuClose as EventListener,
    );

    this.addEventListener(
      SUBMENU_OPEN_EVENT_NAME,
      this.handleSubmenuOpen as EventListener,
    );

    this.addEventListener(
      SUBMENU_CLOSE_EVENT_NAME,
      this.handleSubmenuClose as EventListener,
    );

    this.addEventListener(
      SEARCH_OPEN_EVENT_NAME,
      this.handleSearchOpen as EventListener,
    );

    this.addEventListener(
      SEARCH_CLOSE_EVENT_NAME,
      this.handleSearchClose as EventListener,
    );

    this.#desktopMql.addEventListener(
      "change",
      this.handleDesktopMediaQueryChange,
    );

    this.#motionMql.addEventListener(
      "change",
      this.handleMotionMediaQueryChange,
    );

    window.addEventListener("resize", this.handleWindowResize);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  /**
   * De-activates the focus trap so that keyboard tabbing
   * resumes with the normal document flow.
   */
  deactivateFocusTrap() {
    this.#focusTrap?.deactivate();
    this.#focusTrap = null;
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleWindowResize);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  firstUpdated(): void {
    this.checkAuth();
    this.setAnimationAttributes();
    this.reset();

    this.$menuToggleButton?.addEventListener("click", () => {
      this.toggleMenu();
    });
  }

  handleDesktopMediaQueryChange = ({ matches }: MediaQueryListEvent) => {
    this.useDesktopLayout = matches;
    this.reset();
  };

  handleDocumentClick = (e: MouseEvent) => {
    if (!this.useDesktopLayout) return;

    const shouldCloseSubmenus = !Array.from(this.$submenuTriggers ?? []).find(
      (trigger) => e.composedPath().includes(trigger),
    );

    if (shouldCloseSubmenus) {
      this.closeAllSubmenus();
    }

    const shouldCloseSearch =
      this.$search && !e.composedPath().includes(this.$search);

    if (shouldCloseSearch) {
      this.closeSearch();
    }
  };

  handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        if (!this.useDesktopLayout) {
          this.closeMenu();
        }

        return;
      default:
        return;
    }
  };

  handleMenuButtonClick = (e: MouseEvent) => {
    // Determine the target submenu
    const target = e.currentTarget as HTMLButtonElement;
    const controls = target.getAttribute("aria-controls");
    const $submenu = this.shadowRoot?.querySelector(
      `#${controls}`,
    ) as GlobalHeaderSubmenu;

    if ($submenu.inert) {
      this.openSubmenu($submenu.id);

      // Save the clicked menu item so it can be
      // re-focused after the menu closes. This is
      // required because the menu can be closed my
      // several different triggers, but we only want to
      // return focus to the parent menu item.
      this.#lastFocusedMenuItem = target;
    } else {
      this.closeSubmenu($submenu.id);
    }
  };

  /**
   * Opens the main menu (on mobile).
   */

  handleMenuClose = (e: CustomEvent) => {
    e.stopPropagation();
    this.closeMenu();
  };

  handleMotionMediaQueryChange = ({ matches }: MediaQueryListEvent) => {
    this.useAnimations = !matches;
  };

  handleSearchClose = (e: CustomEvent) => {
    e.stopPropagation();
    this.closeSearch();
  };

  handleSearchOpen = (e: CustomEvent) => {
    e.stopPropagation();
    this.openSearch();
  };

  handleSubmenuClose = (e: CustomEvent<{ id: string }>) => {
    e.stopPropagation();

    this.closeSubmenu(e.detail.id).finally(() => {
      if (!this.useDesktopLayout) {
        this.activateFocusTrap([
          this.$container ?? null,
          this.$menuToggleButton ?? null,
        ]);
      }
    });
  };

  handleSubmenuOpen = (
    e: CustomEvent<{ $trigger?: HTMLElement; id: string }>,
  ) => {
    e.stopPropagation();
    this.openSubmenu(e.detail.id);
  };

  handleWindowResize = () => {
    this.closeAllSubmenus();
  };

  ///////////////
  // HANDLERS  //
  ///////////////

  /**
   * Moves the search element in the DOM so that the
   * tabbing/layout order is appropriate for the state of
   * the matched media.
   */
  moveSearch() {
    if (!this.$search) return;

    if (this.useDesktopLayout) {
      this.$nav?.insertAdjacentElement("afterend", this.$search);
    } else {
      this.$container?.insertAdjacentElement("afterend", this.$search);
    }
  }

  openMenu() {
    if (this.useDesktopLayout) return;

    this.$header?.setAttribute("data-nav-open", "true");

    // Reset the window scroll back to the top.
    requestAnimationFrame(() => {
      window.scroll({ top: -1, left: 0, behavior: "smooth" });
    });

    this.$container?.removeAttribute("inert");
    this.$search?.setAttribute("inert", "true");

    requestIdleCallback(() => {
      this.activateFocusTrap(
        [this.$container ?? null, this.$menuToggleButton ?? null],
        {
          setReturnFocus: this.$menuToggleButton,
        },
      );

      lockDocumentScrolling();
    });
  }

  /**
   * Opens the search box.
   */
  openSearch() {
    if (!this.useDesktopLayout) {
      lockDocumentScrolling();
    }

    this.$header?.setAttribute("data-search-open", "true");
    this.$menuToggleButton?.setAttribute("active", "true");

    // Wait for the focus trap to deactivate
    this.updateComplete.then(() => {
      this.$search?.activate();
    });
  }

  /**
   * Opens a specific submenu.
   */
  openSubmenu(id: string) {
    this.closeAllSubmenus({ exclude: [id] });

    this.$header?.setAttribute("data-submenu-open", "true");

    const $submenu = this.shadowRoot?.querySelector<GlobalHeaderSubmenu>(
      `#${id}`,
    );

    const $trigger = this.shadowRoot?.querySelector<HTMLButtonElement>(
      `[aria-controls="${id}"]`,
    );

    const $li = $trigger?.closest("li");

    // Mark just the active submenu to control the chevron
    $li?.setAttribute("data-submenu-open", "true");

    this.$container?.scrollTo(0, 0);

    if (this.useDesktopLayout && $submenu && $trigger) {
      this.positionSubMenu($submenu, $trigger);
    }

    $submenu?.removeAttribute("inert");

    this.activateFocusTrap([$submenu ?? null, this.$menuToggleButton ?? null], {
      setReturnFocus: $trigger!,
    });

    $trigger?.setAttribute("aria-expanded", "true");

    document.addEventListener("click", this.handleDocumentClick);
  }

  /**
   * Positions the submenu (on desktop) so that it appears
   * underneath the triggering element.
   */
  positionSubMenu($submenu: HTMLElement, $trigger: HTMLElement) {
    const triggerRect = $trigger.getBoundingClientRect();
    const submenuRect = $submenu.getBoundingClientRect();

    const offscreenRight =
      triggerRect.x + submenuRect.width > window.innerWidth + 24;

    const tx = offscreenRight
      ? $trigger.offsetLeft + triggerRect.width - submenuRect.width
      : $trigger.offsetLeft;

    $submenu.style.setProperty("--global-header-submenu-tx-closed", toPx(tx));
    $submenu.style.setProperty("--global-header-submenu-tx-opened", toPx(tx));
  }

  /**
   * Resets elements to their resting states.
   */
  reset() {
    this.setA11yAttributes();
    this.closeMenu();
    this.closeAllSubmenus();
    this.closeSearch();
    this.moveSearch();
    this.resetStyles();

    // Reset the menu toggle to the default state
    this.$menuToggleButton?.reset();
  }

  /**
   * Resets any style modifications that were applied as
   * side-effects.
   */
  resetStyles() {
    this.$submenus?.forEach((x) => {
      x.style.cssText = "";
    });

    // Set the animation delay to a negative value so that
    // the final state is shown initially.
    this.$header?.style.setProperty("--global-header-animation-delay", "-1s");

    setTimeout(() => {
      // Unset the animation delay value after the intial
      // load so that local scope values are used instead.
      this.$header?.style.removeProperty("--global-header-animation-delay");
    }, 1_000);
  }

  /**
   * Updates element a11y attributes to the appropriate
   * resting state based on layout mode.
   */
  setA11yAttributes() {
    if (this.useDesktopLayout) {
      this.$header?.removeAttribute("data-nav-open");
      this.$container?.removeAttribute("inert");
    } else {
      this.$container?.setAttribute("inert", "");
    }

    this.$search?.removeAttribute("inert");

    this.$submenus?.forEach((x) => {
      x.setAttribute("inert", "");
    });

    this.$submenuTriggers?.forEach((x) => {
      x.setAttribute("aria-haspopup", "true");
      x.setAttribute("aria-expanded", "false");
    });
  }

  /**
   * Sets the `data-animation` attributes that connect CSS
   * animations to specific elements.
   */
  setAnimationAttributes() {
    this.$container?.setAttribute("data-animate", "container");
    this.$mainMenu?.setAttribute("data-animate", "menu");
    this.$logo?.setAttribute("data-animate", "logo");
    this.$search?.setAttribute("data-animate", "search");

    this.$submenus?.forEach(($el) => {
      $el.setAttribute("data-animate", "submenu");
    });

    // TODO make this queried members
    this.shadowRoot?.querySelectorAll(".util-item, #give a").forEach(($el) => {
      $el.setAttribute("data-animate", "util-item");
    });

    this.$mainMenu?.querySelectorAll<HTMLElement>("li").forEach(($el, i) => {
      $el.setAttribute("data-animate", "nav-item");
      $el.style.setProperty("--global-header-animation-delay", `${32 * i}ms`);
    });

    // Fully enable animations once the initial keyframes
    // have settled. This is necessary to avoid a flash of
    // animation on initial page load.
    setTimeout(() => {
      this.$header?.setAttribute("data-animation-ready", "true");
    }, 1_000);
  }

  /**
   * Changes the function of the single open/close button based on data-nav-opn
   */
  toggleMenu() {
    const isSearchOpen =
      this.$header?.getAttribute("data-search-open") === "true";

    if (isSearchOpen) {
      this.closeSearch();

      return;
    }

    const isNavOpen = this.$header?.getAttribute("data-nav-open") === "true";

    if (isNavOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  updated() {
    this.$submenuTriggers?.forEach((x) => {
      x.removeEventListener("click", this.handleMenuButtonClick);
      x.addEventListener("click", this.handleMenuButtonClick);
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-global-header": GlobalHeader;
  }
}
